import React, { useState } from 'react';
import { Camera, ChevronDown, ImagePlus, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import ShadeVisualizer from './ShadeVisualizer';
import { SharePreview } from './SharePreview';
import { formatPrice } from '../../lib/utils';
import { resolveImageUrl } from '../../lib/imageCache';

// Compact preview + summary shown above the steps on phones (the desktop left panel is hidden below lg).
export const MobilePreviewPanel = ({
  config, selectedColor, selectedBrand, selectedCollection, selectedFabricMaterial,
  productData, productType, currentVisualization, colorVisualizationImages,
  price, isConfigReady, sizeValid, onRoomPhoto, onAR, onEmail, onChooseFabric,
}) => {
  const [expanded, setExpanded] = useState(true);
  const path = [selectedBrand?.name, selectedCollection?.name, selectedFabricMaterial?.name].filter(Boolean).join(' → ');
  const hasSize = config.width && config.height;

  return (
    <div className="lg:hidden mb-6 rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden" data-testid="mobile-preview-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="mobile-preview-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg border border-neutral-200 overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedColor?.hex_color || '#f5f5f4' }}>
            {selectedColor?.image_url && <img src={resolveImageUrl(selectedColor.image_url, 128)} alt={selectedColor.name} className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-neutral-900 text-sm truncate" data-testid="mobile-preview-fabric">{selectedColor?.name || 'Select a fabric'}</p>
            <p className="text-xs text-neutral-500 truncate">{path || 'Your shade preview'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <p className="font-mono text-sm text-neutral-900" data-testid="mobile-preview-size">{hasSize ? `${config.width}" × ${config.height}"` : 'No size'}</p>
            <p className="font-mono text-xs text-amber-700" data-testid="mobile-preview-price">{isConfigReady ? formatPrice(price.total_price || price.total || 0) : '—'}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <SharePreview selectedColor={selectedColor} productType={productType} config={config}>
          <div className="max-w-[300px] mx-auto rounded-xl overflow-hidden shadow-md" data-testid="mobile-preview-visual">
            <ShadeVisualizer
              onChooseFabric={onChooseFabric}
              config={config}
              selectedColor={selectedColor}
              selectedCollection={selectedCollection}
              selectedMaterial={selectedFabricMaterial}
              productData={productData}
              productType={productType}
              currentVisualization={currentVisualization}
              colorVisualizationImages={colorVisualizationImages}
            />
          </div>
          </SharePreview>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={onRoomPhoto} disabled={!selectedColor} className="text-xs" data-testid="mobile-room-photo-button">
              <ImagePlus className="w-3.5 h-3.5 mr-1" /> Your room
            </Button>
            <Button variant="outline" size="sm" onClick={onAR} disabled={!config.fabric_color || !sizeValid} className="text-xs" data-testid="mobile-ar-button">
              <Camera className="w-3.5 h-3.5 mr-1" /> Try AR
            </Button>
            <Button variant="outline" size="sm" onClick={onEmail} disabled={!selectedColor} className="text-xs" data-testid="mobile-email-button">
              <Mail className="w-3.5 h-3.5 mr-1" /> Email
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePreviewPanel;
